import { useEffect, useId, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Button, Cluster, Icon } from '../../components';
import { ApiError } from '../../lib/api';
import { isIOSPath, useIOSPresentation } from '../../lib/web-presentation';
import type { IdentityContext } from './types';
import type { EvidenceUploadOptions } from './ports';

export const EVIDENCE_FILE_ACCEPT = '.csv,.xlsx,.json,.pdf,.png,.jpg,.jpeg,.webp,text/csv,application/json,application/pdf,image/png,image/jpeg,image/webp,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export interface DocumentUploadDialogProps {
  context: IdentityContext;
  onRegister(file: File, options: EvidenceUploadOptions): Promise<void> | void;
  buttonLabel?: string;
}

interface QueuedDocument {
  id: string;
  file: File;
  /** Browser-side queue identity only. The server records the authoritative
   * custody hash after secure intake. */
  fingerprint?: string;
  state: 'checking' | 'ready' | 'uploading' | 'failed' | 'session-required' | 'duplicate' | 'rejected';
  issue?: string;
}

/** Shared intake for every Amoye Tax product surface. A multi-file selection is
 * registered as independently auditable evidence, one source at a time. */
export function DocumentUploadDialog({ context, onRegister, buttonLabel = 'Add documents' }: DocumentUploadDialogProps) {
  const ios = useIOSPresentation();
  const collection = context.track === 'B2' ? 'Documents' : 'Records';
  const titleId = useId();
  const descriptionId = useId();
  const singleInputId = useId();
  const multipleInputId = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLButtonElement>(null);
  const queueRef = useRef<HTMLElement>(null);
  const queueSequence = useRef(0);
  const activeQueueIds = useRef(new Set<string>());
  const queuedFingerprints = useRef(new Map<string, { id: string; filename: string }>());
  const uploadRun = useRef<AbortController | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [documents, setDocuments] = useState<QueuedDocument[]>([]);
  const [confirmedOwner, setConfirmedOwner] = useState(false);
  const [modelAssistanceRequested, setModelAssistanceRequested] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string>();
  const [selectionNotice, setSelectionNotice] = useState<string>();
  const [checkingBatches, setCheckingBatches] = useState(0);
  const [processing, setProcessing] = useState<{ current: number; total: number; filename: string }>();

  const close = () => {
    uploadRun.current?.abort();
    uploadRun.current = undefined;
    setProcessing(undefined);
    setOpen(false);
    setDocuments([]);
    activeQueueIds.current.clear();
    queuedFingerprints.current.clear();
    setConfirmedOwner(false);
    setModelAssistanceRequested(false);
    setDragActive(false);
    setError(undefined);
    setSelectionNotice(undefined);
  };

  useEffect(() => () => { uploadRun.current?.abort(); }, [context.id]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      openerRef.current?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setDocuments([]);
    activeQueueIds.current.clear();
    queuedFingerprints.current.clear();
    setConfirmedOwner(false);
    setModelAssistanceRequested(false);
    setError(undefined);
    setSelectionNotice(undefined);
  }, [context.id, open]);

  useEffect(() => {
    if (documents.length > 0) queueRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [documents.length]);

  const addFiles = async (selected: FileList | readonly File[]) => {
    const incoming = Array.from(selected);
    if (incoming.length === 0) return;
    const staged = incoming.map((file): QueuedDocument => {
      const id = `staged-${Date.now()}-${queueSequence.current++}`;
      activeQueueIds.current.add(id);
      return isSupportedEvidenceFile(file)
        ? { id, file, state: 'checking' }
        : { id, file, state: 'rejected', issue: 'This file type is not supported.' };
    });
    setDocuments((current) => [...current, ...staged]);
    setSelectionNotice(`${staged.length} document${staged.length === 1 ? '' : 's'} selected. Checking them now…`);
    setConfirmedOwner(false);
    setError(undefined);
    setCheckingBatches((count) => count + 1);
    try {
      for (const document of staged) {
        if (document.state === 'rejected') continue;
        if (!activeQueueIds.current.has(document.id)) continue;
        const fingerprint = await documentFingerprint(document.file);
        if (!activeQueueIds.current.has(document.id)) continue;
        const existing = queuedFingerprints.current.get(fingerprint);
        if (existing) {
          setDocuments((current) => current.map((candidate) => candidate.id === document.id
            ? { ...candidate, fingerprint, state: 'duplicate', issue: `Same content as ${existing.filename}. It will not be uploaded.` }
            : candidate));
          continue;
        }
        queuedFingerprints.current.set(fingerprint, { id: document.id, filename: document.file.name });
        setDocuments((current) => current.map((candidate) => candidate.id === document.id
          ? { ...candidate, fingerprint, state: 'ready', issue: undefined }
          : candidate));
      }
      if (staged.some((document) => activeQueueIds.current.has(document.id))) {
        setSelectionNotice('Checks complete. Review the list before uploading.');
      }
    } finally {
      setCheckingBatches((count) => Math.max(0, count - 1));
    }
  };

  const registerAll = async () => {
    const selected = documents.filter((document) => document.state === 'ready');
    if (!confirmedOwner || selected.length === 0 || checkingBatches > 0 || uploadRun.current) return;
    const controller = new AbortController();
    uploadRun.current = controller;
    const completedIds = new Set<string>(); let nextIndex = 0; let completed = 0; let authenticationFailed = false;
    setProcessing({ current: 0, total: selected.length, filename: 'Preparing independent document jobs' });
    setError(undefined);
    const worker = async () => {
      while (nextIndex < selected.length && !authenticationFailed && !controller.signal.aborted) {
        const index = nextIndex++; const document = selected[index]!;
        setDocuments((current) => current.map((candidate) => candidate.id === document.id
          ? { ...candidate, state: 'uploading', issue: undefined } : candidate));
        try {
          await waitForDocumentIntake(onRegister, document.file, modelAssistanceRequested, controller.signal);
          if (controller.signal.aborted) return;
          completedIds.add(document.id);
          setDocuments((current) => current.map((candidate) => candidate.id === document.id
            ? { ...candidate, state: 'ready', issue: ios ? 'Added to the evidence record.' : 'Added to your supporting records.' } : candidate));
        } catch (cause) {
          if (controller.signal.aborted) return;
          const requiresSignIn = cause instanceof ApiError && cause.status === 401;
          if (requiresSignIn) authenticationFailed = true;
          setDocuments((current) => current.map((candidate) => candidate.id === document.id
            ? { ...candidate, state: requiresSignIn ? 'session-required' : 'failed', issue: requiresSignIn
              ? 'Your sign-in expired before this document was uploaded. Sign in again to continue.'
              : cause instanceof Error ? cause.message
              : 'This document could not be added. Try it again.' } : candidate));
        } finally {
          completed += 1;
          if (!controller.signal.aborted) setProcessing({ current: completed, total: selected.length, filename: document.file.name });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, selected.length) }, () => worker()));
    if (controller.signal.aborted || uploadRun.current !== controller) return;
    uploadRun.current = undefined;
    if (authenticationFailed) {
      setDocuments((current) => current.map((candidate) => selected.some(({ id }) => id === candidate.id)
        && !completedIds.has(candidate.id) && candidate.state === 'ready'
        ? { ...candidate, state: 'session-required',
          issue: 'Your sign-in expired before this document was uploaded. Sign in again to continue.' }
        : candidate));
    }
    for (const document of selected.filter((candidate) => completedIds.has(candidate.id))) {
      activeQueueIds.current.delete(document.id);
      if (document.fingerprint && queuedFingerprints.current.get(document.fingerprint)?.id === document.id) {
        queuedFingerprints.current.delete(document.fingerprint);
      }
    }
    const failed = selected.length - completedIds.size;
    setDocuments((current) => current.filter((document) => !completedIds.has(document.id)));
    setProcessing(undefined);
    setConfirmedOwner(false);
    if (failed === 0) {
      setOpen(false); setSelectionNotice(undefined);
    } else if (authenticationFailed) {
      setError('Your sign-in expired. Sign in again before uploading these documents. Retrying them from this screen will not fix the session.');
      setSelectionNotice(`${completedIds.size} of ${selected.length} documents were added before the session ended.`);
    } else {
      setError(`${failed} document${failed === 1 ? '' : 's'} could not be added. Review the message, then retry or remove each one.`);
      setSelectionNotice(`${completedIds.size} of ${selected.length} documents were added successfully.`);
    }
  };

  const readyCount = documents.filter((document) => document.state === 'ready').length;
  const checkingCount = documents.filter((document) => document.state === 'checking').length;
  const attentionCount = documents.filter((document) => ['duplicate', 'rejected', 'failed', 'session-required'].includes(document.state)).length;
  const selectedCount = readyCount + checkingCount;

  return <>
    <Button iconBefore={<Icon name="files" size={17} />} onClick={() => setOpen(true)} ref={openerRef}>{buttonLabel}</Button>
    {open && createPortal(<div className="am-document-dialog__backdrop" onMouseDown={(event) => {
      if (event.target === event.currentTarget) close();
    }}>
      <section aria-describedby={descriptionId} aria-labelledby={titleId} aria-modal="true"
        className="am-document-dialog" onKeyDown={(event) => {
          if (event.key === 'Escape') { event.preventDefault(); close(); }
          if (event.key === 'Tab') trapDialogFocus(event, dialogRef.current);
        }} ref={dialogRef} role="dialog">
        <header className="am-document-dialog__header">
          <div><p className="am-eyebrow">{ios ? 'Secure evidence intake' : 'Secure document upload'}</p><h2 id={titleId}>Add documents</h2>
            <p id={descriptionId}>Choose one file, select several files, or drag a group into the area below.</p></div>
          <button aria-label="Close document upload" className="am-document-dialog__close"
            onClick={close} ref={closeRef} type="button"><Icon name="close" size={20} /></button>
        </header>

        <div className="am-document-dialog__body">
          <div className="am-client-scope-banner" role="status"><span>Adding for</span><strong>{context.subjectName}</strong>
            <small>{context.packageLabel} · {context.periodLabel}</small></div>

          {documents.length > 0 && <section aria-label="Selected documents" className="am-document-queue" ref={queueRef}>
            <header><div><strong>{selectedCount} document{selectedCount === 1 ? '' : 's'} selected</strong>
              <small>{readyCount} ready{checkingCount > 0 ? ` · ${checkingCount} checking` : ''}{attentionCount > 0 ? ` · ${attentionCount} needs attention` : ''}</small></div>
              <button disabled={Boolean(processing)} onClick={() => {
                setDocuments([]); activeQueueIds.current.clear(); queuedFingerprints.current.clear();
                setConfirmedOwner(false); setSelectionNotice(undefined);
              }} type="button">Clear all</button></header>
            <ul>{documents.map((document) => <li className={`am-document-queue__item am-document-queue__item--${document.state}`} key={document.id}>
              <span className="am-document-queue__file"><strong title={document.file.name}>{document.file.name}</strong>
                <small>{formatBytes(document.file.size)}</small>{document.issue && <small className="am-document-queue__issue">{document.issue}</small>}</span>
              <span className="am-document-queue__actions"><span aria-live="polite" className={`am-document-queue__state am-document-queue__state--${document.state}`}>
                {queueStateLabel(document.state)}</span>
                {document.state === 'failed' && <button disabled={Boolean(processing)} onClick={() => {
                  setDocuments((current) => current.map((candidate) => candidate.id === document.id
                    ? { ...candidate, state: 'ready', issue: undefined } : candidate));
                  setError(undefined); setConfirmedOwner(false);
                }} type="button"><span>Retry</span></button>}
                <button aria-label={`Remove ${document.file.name}`} disabled={Boolean(processing)} onClick={() => {
                  removeQueuedDocument(document, activeQueueIds.current, queuedFingerprints.current, setDocuments);
                  setConfirmedOwner(false);
                  setSelectionNotice(`${document.file.name} removed. You can add another document.`);
                }} type="button"><Icon name="close" size={15} /><span>Remove</span></button></span>
            </li>)}</ul>
            <footer>{processing ? (ios ? 'You can close or cancel. Files already received are kept and processed in Evidence; remaining transfers stop.'
              : `You can close or cancel. Files already received are kept and processed in ${collection}; remaining transfers stop.`)
              : 'Nothing is uploaded until you select the final upload button.'}</footer>
          </section>}

          {selectionNotice && <p aria-live="polite" className="am-document-selection-notice">{selectionNotice}</p>}

          <div className="am-document-dialog__choices">
            <label aria-disabled={Boolean(processing)} className="am-file-choice" htmlFor={singleInputId}
              onKeyDown={(event) => activateFileChoice(event, singleInputId)} role="button" tabIndex={processing ? -1 : 0}><Icon name="file-text" size={19} />
              <span><strong>Choose one file</strong><small>Add documents one at a time</small></span></label>
            <input accept={EVIDENCE_FILE_ACCEPT} className="am-visually-hidden" disabled={Boolean(processing)} id={singleInputId}
              onChange={(event) => { if (event.target.files) void addFiles(event.target.files); event.target.value = ''; }} tabIndex={-1} type="file" />
            <label aria-disabled={Boolean(processing)} className="am-file-choice" htmlFor={multipleInputId}
              onKeyDown={(event) => activateFileChoice(event, multipleInputId)} role="button" tabIndex={processing ? -1 : 0}><Icon name="files" size={19} />
              <span><strong>Select multiple files</strong><small>Choose several documents together</small></span></label>
            <input accept={EVIDENCE_FILE_ACCEPT} className="am-visually-hidden" disabled={Boolean(processing)} id={multipleInputId} multiple
              onChange={(event) => { if (event.target.files) void addFiles(event.target.files); event.target.value = ''; }} tabIndex={-1} type="file" />
          </div>

          <div aria-disabled={Boolean(processing)} aria-label="Document drop area" className={`am-document-dropzone${dragActive ? ' is-active' : ''}${documents.length > 0 ? ' has-selection' : ''}`}
            onDragEnter={(event) => handleDrag(event, true, setDragActive)}
            onDragLeave={(event) => handleDrag(event, false, setDragActive)}
            onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }}
            onDrop={(event) => {
              event.preventDefault(); event.stopPropagation(); setDragActive(false);
              if (processing) return;
              const dropped = droppedFiles(event.dataTransfer);
              if (dropped.length === 0) {
                setError('No files were detected. Drop individual files rather than a folder, or use Select multiple files.');
                return;
              }
              void addFiles(dropped);
            }}
            onKeyDown={(event) => activateFileChoice(event, multipleInputId)} role="button" tabIndex={processing ? -1 : 0}>
            <Icon name="arrow-down" size={24} /><strong>{documents.length > 0 ? 'Drag more documents here' : 'Drag and drop documents here'}</strong>
            <span>CSV, Excel, JSON, PDF, PNG, JPG and WebP</span>
          </div>

          {readyCount > 0 && <label className="am-document-owner-confirmation"><input checked={confirmedOwner}
            disabled={Boolean(processing) || checkingCount > 0} onChange={(event) => setConfirmedOwner(event.target.checked)} type="checkbox" />
            <span><strong>Confirm the taxpayer</strong>All selected documents belong to {context.subjectName}. Only documents marked Ready will be uploaded, and Amoye will block a document if another managed taxpayer is detected.</span></label>}

          {readyCount > 0 && <label className="am-document-owner-confirmation"><input checked={modelAssistanceRequested}
            disabled={Boolean(processing) || checkingCount > 0} onChange={(event) => setModelAssistanceRequested(event.target.checked)} type="checkbox" />
            <span><strong>Use optional model assistance</strong>Allow Amoye’s approved classification model to suggest a category only when the published rules cannot. Every suggestion still requires review, and the model cannot calculate tax or change source amounts.</span></label>}

          {processing && <p aria-live="polite" className="am-document-progress">Processing {processing.current} of {processing.total}: <strong>{processing.filename}</strong></p>}
          {error && <p aria-live="assertive" className="am-document-error" role="alert">{error}</p>}
        </div>

        <footer className="am-document-dialog__footer"><span>Each document receives its own custody record and review state.</span>
          <Cluster><Button onClick={close} tone="secondary">Cancel</Button>
            <Button disabled={!confirmedOwner || readyCount === 0 || checkingBatches > 0} loading={Boolean(processing)} onClick={() => void registerAll()}>
              {processing ? `Uploading ${processing.current} of ${processing.total}` : readyCount > 0
                ? `Upload ${readyCount} document${readyCount === 1 ? '' : 's'}` : 'Upload documents'}</Button></Cluster>
        </footer>
      </section>
    </div>, document.body)}
  </>;
}

/** Finish at durable intake, not after OCR. Cancelling never deletes accepted sources. */
export function waitForDocumentIntake(register: DocumentUploadDialogProps['onRegister'], file: File,
  modelAssistanceRequested: boolean, signal: AbortSignal, timeoutMs = 120_000): Promise<void> {
  return new Promise((resolve, reject) => {
    const transfer = new AbortController();
    let settled = false;
    let timer: ReturnType<typeof setTimeout>;
    const finish = (error?: unknown) => {
      if (settled) return;
      settled = true; clearTimeout(timer); signal.removeEventListener('abort', cancel);
      if (error) reject(error); else resolve();
    };
    const cancel = () => { transfer.abort(); finish(new DOMException('Upload cancelled', 'AbortError')); };
    timer = setTimeout(() => {
      transfer.abort();
      finish(new Error(isIOSPath()
        ? 'Upload confirmation is taking too long. Close this dialog and check Evidence before retrying: the service may already have received this file.'
        : 'Upload confirmation is taking too long. Close this dialog and check your uploaded documents before retrying: the service may already have received this file.'));
    }, timeoutMs);
    signal.addEventListener('abort', cancel, { once: true });
    if (signal.aborted) { cancel(); return; }
    Promise.resolve().then(() => register(file, { modelAssistanceRequested, signal: transfer.signal,
      onAccepted: () => finish() })).then(() => finish(), finish);
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function queueStateLabel(state: QueuedDocument['state']): string {
  if (state === 'checking') return 'Checking';
  if (state === 'uploading') return 'Uploading';
  if (state === 'failed') return 'Needs retry';
  if (state === 'session-required') return 'Sign in required';
  if (state === 'ready') return 'Ready';
  if (state === 'duplicate') return 'Duplicate';
  return 'Not supported';
}

function isSupportedEvidenceFile(file: File): boolean {
  return /\.(csv|xlsx|json|pdf|png|jpe?g|webp)$/i.test(file.name);
}

function removeQueuedDocument(document: QueuedDocument, activeIds: Set<string>,
  fingerprints: Map<string, { id: string; filename: string }>,
  update: (updater: (current: QueuedDocument[]) => QueuedDocument[]) => void): void {
  activeIds.delete(document.id);
  const fingerprint = document.fingerprint;
  if (!fingerprint || fingerprints.get(fingerprint)?.id !== document.id) {
    update((current) => current.filter((candidate) => candidate.id !== document.id));
    return;
  }
  fingerprints.delete(fingerprint);
  update((current) => {
    const remaining = current.filter((candidate) => candidate.id !== document.id);
    const replacement = remaining.find((candidate) => candidate.fingerprint === fingerprint && candidate.state === 'duplicate');
    if (!replacement) return remaining;
    fingerprints.set(fingerprint, { id: replacement.id, filename: replacement.file.name });
    return remaining.map((candidate) => {
      if (candidate.id === replacement.id) return { ...candidate, state: 'ready', issue: undefined };
      if (candidate.fingerprint === fingerprint && candidate.state === 'duplicate') return {
        ...candidate, issue: `Same content as ${replacement.file.name}. It will not be uploaded.`,
      };
      return candidate;
    });
  });
}

async function documentFingerprint(file: File): Promise<string> {
  try {
    if (!globalThis.crypto?.subtle || typeof file.arrayBuffer !== 'function') throw new Error('CONTENT_HASH_UNAVAILABLE');
    const digest = await globalThis.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return `sha256:${[...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('')}`;
  } catch {
    // Metadata still prevents the same browser-selected file being queued more
    // than once. Secure intake performs the authoritative content hash.
    return `metadata:${file.name}:${file.size}:${file.lastModified}`;
  }
}

function handleDrag(event: DragEvent, active: boolean, setActive: (value: boolean) => void): void {
  event.preventDefault();
  event.stopPropagation();
  setActive(active);
}

function droppedFiles(dataTransfer: DataTransfer): File[] {
  const direct = Array.from(dataTransfer.files ?? []);
  if (direct.length > 0) return direct;
  return Array.from(dataTransfer.items ?? []).flatMap((item) => {
    if (item.kind !== 'file') return [];
    const file = item.getAsFile();
    return file ? [file] : [];
  });
}

function activateFileChoice(event: KeyboardEvent, inputId: string): void {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  document.getElementById(inputId)?.click();
}

function trapDialogFocus(event: KeyboardEvent, dialog: HTMLElement | null): void {
  if (!dialog) return;
  const focusable = [...dialog.querySelectorAll<HTMLElement>('button:not([disabled]), [tabindex="0"], input:not([disabled]):not([tabindex="-1"])')];
  if (focusable.length === 0) return;
  const first = focusable[0]!;
  const last = focusable.at(-1)!;
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
}
